import React, { useState } from 'react';
import { Grid, Stack, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { startOfWeek } from 'date-fns';
import { NavBarContainer, NavBarTitle } from 'common/NavBar';
import useOrganizationStartWeekDay from 'hooks/useOrganizationStartWeekDay';
import ContentSuspense from 'common/ContentSuspense/ContentSuspense';
import useGetEmployees from 'hooks/useGetEmployees';
import ScheduleNavigator from './ScheduleNavigator/ScheduleNavigator';
import RangeScaleDays from './RangeScaleDays/RangeScaleDays';
import generateRangeDays from './generateRangeDays';
import EmployeeWorkSchedule from './EmployeeWorkSchedule/EmployeeWorkSchedule';
import { PlannerEmployee } from './interfaces';

export default function WorkSchedulesPlanner() {
  const startWeekDay = useOrganizationStartWeekDay();
  const [currentDate, setCurrentDate] = useState(
    startOfWeek(new Date(), {
      weekStartsOn: startWeekDay,
    }),
  );
  const {
    data: employees = [],
    isLoading: employeesLoading,
    isError: employeesError,
  } = useGetEmployees<PlannerEmployee>({
    properties: ['lastname', 'firstname', 'avatarUrl', 'position', 'id'],
    isActive: true,
  });

  const { t } = useTranslation();
  const rangeDays = generateRangeDays({
    currentDate,
    startDayOfWeek: startWeekDay,
  });

  return (
    <Grid item xs={10} bgcolor="background.list">
      <NavBarContainer>
        <NavBarTitle>{t('settings.work_schedules')}</NavBarTitle>
      </NavBarContainer>
      <ContentSuspense isLoading={employeesLoading} isError={employeesError}>
        <Box borderBottom="0.1rem solid" borderColor="border.main">
          <ScheduleNavigator
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
          />
          <RangeScaleDays rangeDays={rangeDays} employees={employees} />
          <Stack spacing={1} bgcolor="border.divider">
            {employees.map((employee, index) => (
              <EmployeeWorkSchedule
                key={employee.id}
                currentDate={currentDate}
                hideBorderTop={!index}
                employee={employee}
                rangeDays={rangeDays}
              />
            ))}
          </Stack>
        </Box>
      </ContentSuspense>
    </Grid>
  );
}
